<script setup lang="ts">
import { colorModeForPath } from '~/composables/useColorMode'
import { isMarketingPath } from '~/utils/marketingRoutes'
import { themeColorMeta } from '~/utils/themeColor'

const { preference, resolved } = useColorMode()
const { locale } = useI18n()
const route = useRoute()

// Public marketing pages are always light (MAR-1192) and carry `html.marketing`, while `/app/**`,
// `/login` and `/addin` follow the user's appearance setting. Typography (`Geist` + `EB Garamond` +
// `Roboto Mono`) is unified across all routes (MAR-1343). Bound through `useHead`, so the server
// render carries it from the first byte and client navigation updates it in place.
const htmlClass = computed(() => {
  if (isMarketingPath(route.path)) return 'marketing'
  return colorModeForPath(resolved.value, route.path) === 'dark' ? 'dark' : ''
})

// Browser / PWA chrome colour (MAR-1316). `nuxt.config.ts` has the two OS-scoped tags; an explicit
// Appearance setting that differs from the OS is swapped in here. Marketing pages and "system" push
// nothing, so they keep the config tags.
const themeColor = computed(() => themeColorMeta(preference.value, resolved.value, route.path))

// Page-title scheme: "Marcus" by default, "Marcus - <page>" when a page sets a
// title. The marketing/home tagline is opted in only by app/pages/index.vue
// setting `landing.metaTitle` as its title.
//
// `<html lang>` follows the locale the page is rendering (MAR-979). `nuxt.config.ts` pins `es` for
// the very first byte; this binding overrides it on the server render (the cookie has already been
// applied by `plugins/i18n-persist.ts`) and on every in-place language switch after that.
useHead({
  titleTemplate: title => (title ? `Marcus - ${title}` : 'Marcus'),
  htmlAttrs: { lang: locale, class: htmlClass },
  meta: themeColor,
})
</script>

<template>
  <NuxtLoadingIndicator color="var(--color-primary)" />
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>
